import React, { useEffect, useRef, useState } from "react";
import { Constants } from "@eagerdog/constants";
import { IEvent, IEventSummary, IFeedEventSummary, IFeedPostItem, IPost } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";
import Button from "src/components/Button/Button";
import Icon from "src/components/Icon/Icon";
import TextArea from "src/components/TextArea/TextArea";
import ClubPostCard from "../FeedItem/ClubPostCard";
import { notifyPostCreated, showPostError } from "../../postFeedback";

import styles from "./ShareToFeedModal.module.scss";

interface IProps {
    event: IEvent;
    onClose: () => void;
}

const MAX_BODY = 2000;
const WARN_BODY = 1800;
const FLYER_TYPES = ["image/jpeg", "image/png", "image/webp"];
const FLYER_MAX_BYTES = 1048576 * 10;

/** Live summary for the preview, built the same way the feed endpoint does. */
const toFeedEventSummary = (event: IEvent | IEventSummary): IFeedEventSummary => {
    const summary = event as IEventSummary;
    const now = Date.now();
    let minFee: number | undefined;
    for (const st of summary.show_types || []) {
        const earlyBird = st.early_bird_fee != null && st.early_bird_end_date && now <= new Date(st.early_bird_end_date).getTime();
        const fee = earlyBird ? st.early_bird_fee : st.fee;
        if (fee != null && (minFee === undefined || fee < minFee)) minFee = fee;
    }
    return {
        status: event.status,
        publish_at: event.publish_at,
        registration_end_date: event.registration_end_date,
        address_line1: event.address?.line1,
        show_types: [...new Set((summary.show_types || []).map((st) => st.show_type).filter(Boolean))],
        min_fee: minFee,
        currency: summary.currency,
    };
};

/** Default post shape used for the preview until the server's post arrives. */
const placeholderPost = (event: IEvent): IPost => ({
    _id: "preview",
    author_type: Constants.post_author_type.club,
    author_id: event.club_id?.toString(),
    author_display_name: event.club_name,
    post_type: Constants.post_type.event_published,
    linked_event_id: event._id?.toString(),
    share_count: 0,
    created_at: new Date(),
    activity: {
        event_id: event._id?.toString(),
        event_handle: event.handle,
        event_title: event.title,
        sanctioning_club: event.sanctioning_club,
        club_id: event.club_id?.toString(),
        club_name: event.club_name,
        start_date: event.start_date,
        end_date: event.end_date,
        city: event.address?.city,
        state: event.address?.state,
    },
} as unknown as IPost);

/**
 * Shown right after a club publishes an event. Lets them add a message and a
 * flyer to the feed post, with a live preview rendered by the real card.
 */
const ShareToFeedModal: React.FC<IProps> = ({ event, onClose }) => {
    const clubId = event.club_id?.toString();
    const eventId = event._id?.toString();

    const [post, setPost] = useState<IPost>(placeholderPost(event));
    const [summary, setSummary] = useState<IFeedEventSummary>(toFeedEventSummary(event));
    const [body, setBody] = useState("");
    const [flyerUrl, setFlyerUrl] = useState<string>(event.flyer_image_url || "");
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);

    // Load the post the publish hook created (retry once for the race) and
    // the full summary so the preview shows show types and prices.
    useEffect(() => {
        let cancelled = false;
        const loadPost = async (attempt = 0): Promise<void> => {
            try {
                const serverPost = await apiService.getEventFeedPost(clubId, eventId);
                if (cancelled) return;
                setPost(serverPost);
                if (serverPost.body) setBody(serverPost.body);
                const image = serverPost.media?.find((m) => m.type === Constants.post_media_type.image)?.url;
                if (image) setFlyerUrl(image);
            } catch {
                if (!cancelled && attempt === 0) {
                    setTimeout(() => loadPost(1), 500);
                }
            }
        };
        loadPost();
        if (event.handle) {
            apiService.getEventSummaryForHandle(event.handle)
                .then((full) => { if (!cancelled) setSummary(toFeedEventSummary(full)); })
                .catch(() => { /* preview falls back to the basic summary */ });
        }
        return () => { cancelled = true; };
    }, [clubId, eventId, event.handle]);

    const previewPost: IFeedPostItem = {
        ...(post as IFeedPostItem),
        type: Constants.feed_item_type.post,
        body: body.trim() || undefined,
        media: flyerUrl ? [{ type: Constants.post_media_type.image, url: flyerUrl }] : [],
        event: summary,
        like_count: 0,
        liked_by_viewer: false,
        viewer_can_delete: false,
        moderation_status: "visible",
    } as IFeedPostItem;

    const onPickFlyer = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!FLYER_TYPES.includes(file.type)) {
            toast.show({ title: "Flyer Upload", content: "Please choose a JPG, PNG or WebP image", duration: 8000, type: "fail" });
            return;
        }
        if (file.size > FLYER_MAX_BYTES) {
            toast.show({ title: "Flyer Upload", content: "Image is too big, please select one under 10 MB", duration: 8000, type: "fail" });
            return;
        }
        setUploading(true);
        apiService.uploadFile(file)
            .then((response: any) => {
                const url = typeof response === "string" ? response : response?.value;
                if (typeof url !== "string" || !url) throw new Error("Upload did not return a file URL");
                setFlyerUrl(url);
            })
            .catch(() => {
                toast.show({ title: "Flyer Upload", content: "Could not upload the flyer, please try again", duration: 8000, type: "fail" });
            })
            .finally(() => setUploading(false));
    };

    const share = () => {
        setSaving(true);
        apiService.updateEventFeedPost(clubId, eventId, {
            body: body.trim(),
            flyer_image_url: flyerUrl,
        })
            .then((saved) => {
                if (saved.moderation_status === "under_review") {
                    notifyPostCreated(saved);
                } else {
                    toast.show({ title: "Shared to your feed", content: "Followers will see this on their home feed.", duration: 6000, type: "success" });
                }
                onClose();
            })
            .catch((err) => {
                showPostError(err, { title: "Share failed", content: "Unable to update your feed post right now. Please try again." });
            })
            .finally(() => setSaving(false));
    };

    const count = body.length;

    return (
        <div className={styles.ShareToFeedModal}>
            <div className={styles.title}>Share to your feed</div>
            <p className={styles.subtitle}>
                Followers of {event.club_name || "your club"} will see this on their home feed. Add a message or a flyer, or skip.
            </p>

            <div className={styles.preview} aria-label="Post preview">
                <ClubPostCard post={previewPost} onChange={() => {}} />
            </div>

            <TextArea
                className={styles.message}
                label="Message (optional)"
                placeholder="Tell followers why they should enter…"
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
            />
            <div className={`${styles.counter} ${count >= WARN_BODY ? styles.counterWarn : ""}`}>{count}/{MAX_BODY}</div>

            <div className={styles.flyerSection}>
                <input ref={fileInput} type="file" accept={FLYER_TYPES.join(",")} onChange={onPickFlyer} hidden />
                {!flyerUrl && (
                    <div className={styles.flyerPrompt}>
                        <Icon type="camera" className={styles.flyerIcon} />
                        <div className={styles.flyerText}>
                            <span className={styles.flyerTitle}>Add a flyer so your event stands out</span>
                            <span className={styles.flyerHint}>JPG, PNG or WebP, up to 10 MB.</span>
                        </div>
                        <Button variant="secondary" isLoading={uploading} onClick={() => fileInput.current?.click()}>Upload flyer</Button>
                    </div>
                )}
                {flyerUrl && (
                    <div className={styles.flyerCurrent}>
                        <img src={flyerUrl} alt="Event flyer" />
                        <div className={styles.flyerActions}>
                            <Button variant="secondary" isLoading={uploading} onClick={() => fileInput.current?.click()}>Replace</Button>
                            <Button variant="tertiary" disabled={uploading} onClick={() => setFlyerUrl("")}>Remove</Button>
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.actions}>
                <Button variant="secondary" disabled={saving} onClick={onClose}>Skip</Button>
                <Button isLoading={saving} disabled={uploading} onClick={share}>Share</Button>
            </div>
        </div>
    );
};

export default ShareToFeedModal;
